package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.shared.MtJobState
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckCheckIcon
import dev.lingvo.site.ds.LEyeIcon
import dev.lingvo.site.ds.LStatTile
import dev.lingvo.site.ds.LTaskProgress
import dev.lingvo.site.ds.LWandSparklesIcon
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.trf

internal const val EDITOR_QUEUE_PRETRANSLATE_ID = "lingvo-editor-queue-pretranslate"
internal const val EDITOR_QUEUE_APPROVE_CLEAN_ID = "lingvo-editor-queue-approve-clean"

/**
 * The queue's counts, and the one or two automations those counts imply.
 *
 * **Absent, never disabled.** Neither control is drawn unless the verified authority holds the
 * exact privilege it needs — `canSpendCredits` for the run, `canApproveTranslations` for the batch
 * — and neither is drawn unless the count beneath it is real. A greyed button that explains itself
 * in a tooltip is still a promise the server will refuse.
 *
 * **One primary action per screen, and this is not it.** The editor's primary is the action bar's
 * "Approve and next" / "Save and next", which carries `data-editor-primary`. These are `SECONDARY`
 * — loud enough to find while scanning the counts, quiet enough not to compete with the button that
 * ends the loop on the string somebody is actually typing.
 */
@Composable
internal fun EditorQueueActions(
    state: TranslationEditorState,
    filtered: Boolean,
    onOpenBulkConfirm: (EditorFocusRef) -> Unit,
) {
    val automation = state.queueAutomation
    val tiles = editorQueueCountTiles(
        projectLoaded = state.project != null,
        progress = state.progress,
        total = state.total,
        locale = state.locale,
        filtered = filtered,
    )
    val pretranslate = editorPretranslateScope(
        query = state.currentQuery,
        progress = state.progress,
        total = state.total,
        locale = state.locale,
        provider = state.enabledProviders.firstOrNull(),
    ).takeIf { state.capability.canSpendCredits }
    val approveItems = if (state.capability.canApproveTranslations) {
        editorApproveCleanCandidates(state.rows.toList(), state.qaIssues.toList(), state.sourceAwareQueueCaptured)
    } else {
        emptyList()
    }
    val filePath = state.project?.files?.firstOrNull { it.id == state.fileId }?.path
    val scopeSentence = editorScopeSentence(state.currentQuery, filePath, state.locale)

    Row(
        EditorQueueCountsStyle.toModifier().attr("data-editor-queue-counts", "true"),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        tiles.forEach { tile ->
            LStatTile(label = tile.label, value = tile.value, compact = true)
        }
        // **The one read-only explanation this surface gets.** Every mutating control above is
        // absent rather than disabled for a viewer, which is right and also silent — without this
        // the queue looks identical to a writer's and the missing buttons read as a bug.
        if (!state.capability.canWriteTranslations && state.project != null) {
            LBadge(
                text = t("editor.editor_queue_actions.read_only", "Read-only"),
                modifier = Modifier.attr("data-editor-queue-read-only", "true"),
                leading = { LEyeIcon() },
            )
        }
        Row(
            EditorQueueActionSlotStyle.toModifier().attr("data-editor-queue-actions", "true"),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            if (pretranslate != null) {
                LButton(
                    label = tPlural(
                        "editor.editor_queue_actions.pre_translate_count",
                        pretranslate.untranslated,
                        "Pre-translate {count}",
                        "Pre-translate {count}",
                    ),
                    onClick = {
                        automation.openPretranslate(scopeSentence, pretranslate)
                        onOpenBulkConfirm(EditorFocusRef.ElementId(EDITOR_QUEUE_PRETRANSLATE_ID))
                    },
                    modifier = Modifier
                        .then(EditorCoarseTargetStyle.toModifier())
                        .attr("id", EDITOR_QUEUE_PRETRANSLATE_ID)
                        .attr("data-editor-coarse-target", "queue-action"),
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LWandSparklesIcon() },
                    loading = automation.starting,
                )
            }
            // **No minimum.** The `>= 2` floor this carried is the *review lane's* gate (spec
            // §7.2, "ADMIN or `canApprove`, >=2 eligible"). Spec §7.1 states the editor's condition
            // as "when `canApproveTranslations` and the scope contains PENDING rows" — so with one
            // eligible string the button used to vanish with no explanation, and the reader had to
            // open the string and use the action bar to do what the header offered a moment ago.
            if (approveItems.isNotEmpty()) {
                LButton(
                    label = tf(
                        "editor.editor_queue_actions.approve_qa_clean",
                        "Approve QA-clean ({count})",
                        "count" to approveItems.size.toString(),
                    ),
                    onClick = {
                        automation.openApproveClean(
                            scopeSentence,
                            approveItems,
                            editorBatchPreviewNames(state.rows.toList(), approveItems),
                            editorSourceApprovalItems(state.rows.toList(), approveItems, state.sourceAwareQueueCaptured),
                        )
                        onOpenBulkConfirm(EditorFocusRef.ElementId(EDITOR_QUEUE_APPROVE_CLEAN_ID))
                    },
                    modifier = Modifier
                        .then(EditorCoarseTargetStyle.toModifier())
                        .attr("id", EDITOR_QUEUE_APPROVE_CLEAN_ID)
                        .attr("data-editor-coarse-target", "queue-action"),
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LCheckCheckIcon() },
                )
            }
        }
    }

    val job = automation.job
    if (job != null && job.state == MtJobState.RUNNING) {
        Row(
            EditorQueueRunStyle.toModifier().attr("data-editor-queue-run", job.id),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            LTaskProgress(
                done = job.done,
                total = job.total,
                failed = job.failed + job.rejected,
                label = t("editor.editor_queue_actions.pre_translate_run", "Pre-translate run"),
            )
        }
    }
}

/**
 * What this run or batch covers, in the reader's own filter words.
 *
 * Built from `appliedEditorFilters`, the same list the header's chips are drawn from, so the
 * sentence in the confirm and the chips above it can never describe different sets. With nothing
 * applied it names the language, because "every untranslated string" without saying in what is the
 * sentence that makes somebody translate the wrong locale.
 */
internal fun editorScopeSentence(
    query: EditorQuery,
    filePath: String?,
    locale: String,
): String {
    val applied = appliedEditorFilters(query, filePath)
    if (applied.isEmpty()) {
        return trf(
            "editor.editor_queue_actions.scope_whole_locale",
            "Everything in {language}.",
            "language" to localeInfo(locale).englishName,
        )
    }
    return trf(
        "editor.editor_queue_actions.scope_filters",
        "{language}, filtered to {filters}.",
        "language" to localeInfo(locale).englishName,
        "filters" to applied.joinToString(" · ") { it.label },
    )
}
